// =====================================================================================
// INVRT Radar - web console - operator catalogue section (Story PS1 rebuild)
//
// The catalogue is the product, service, module and campaign hierarchy every subscription
// and grant points at. Desktop renders the canonical DataTable mirror; narrow viewports
// render the canonical MobileRows mirror; status renders as canonical Chips.
//
// Pagination law (Story PS1): each of the four flat E03 pagers is consumed page by page
// with the server's REAL cursors - one unified catalogue page shows the tree assembled
// from page N of every kind that still has pages - instead of reading every page into
// memory and rendering every row. Hierarchy is conveyed by data (the Level column and an
// explicit "under {parent}" line), never by indent padding; a row's parent is marked
// unavailable only once the parent kind is exhausted, because a missing parent while
// pages remain is "not loaded yet", not "missing".
//
// Dialog parent pickers need complete option sets, which the seam can only provide by
// walking the parent pagers; that walk happens once, on demand, when the create dialog
// opens (fixtures and small catalogues are already complete, so it costs nothing there).
// =====================================================================================

import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Chip, type ChipSignal } from "@/components/ui/chip";
import { DataTable, type DataTableRow } from "@/components/ui/data-table";
import { Input, Select, Textarea } from "@/components/ui/input";
import { MobileRows, type MobileRow } from "@/components/ui/mobile-rows";
import { Pagination } from "@/components/ui/pagination";
import { useToast } from "@/components/ui/toast";
import { useApiClient } from "@/hooks/useApi";
import { isApiError } from "@/lib/api";
import {
  CATALOGUE_KINDS,
} from "@/lib/types.entitlements";
import { fetchCataloguePage, invalidContract, parseMutationResponse } from "@/pages/operator/catalogueReader";
import type {
  AdminCatalogueResponse,
  CampaignCatalogueItem,
  CatalogueItem,
  CatalogueKind,
  CataloguePage,
  CatalogueStatus,
  ModuleCatalogueItem,
  ProductCatalogueItem,
  ServiceCatalogueItem,
} from "@/lib/types.entitlements";

type CatalogueDialog =
  | { action: "create" }
  | { action: "edit"; kind: CatalogueKind; item: CatalogueItem }
  | { action: "retire"; kind: CatalogueKind; item: CatalogueItem };

interface CatalogueDisplayRow {
  kind: CatalogueKind;
  item: CatalogueItem;
  parentMissing: boolean;
  parentName: string | undefined;
}

interface MutationRequest {
  action: "create" | "edit" | "retire";
  kind: CatalogueKind;
  item?: CatalogueItem;
  body: Record<string, unknown>;
}

const KIND_LABELS: Record<CatalogueKind, string> = {
  product: "Product",
  service: "Service",
  module: "Module",
  campaign: "Campaign",
};

// ---- the real-cursor pager -----------------------------------------------------------------

interface KindPageState {
  items: CatalogueItem[];
  nextCursor: string | null;
}

type PagesByKind = Record<CatalogueKind, KindPageState[]>;

const EMPTY_PAGES: PagesByKind = { product: [], service: [], module: [], campaign: [] };

const COMPLETE_ALL: Record<CatalogueKind, boolean> = { product: true, service: true, module: true, campaign: true };

function useCataloguePager() {
  const api = useApiClient();
  const [pagesByKind, setPagesByKind] = useState<PagesByKind>(EMPTY_PAGES);
  const [page, setPage] = useState(0);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<unknown>(null);
  const [failedAction, setFailedAction] = useState<"initial" | "next">("initial");

  const fetchPage = useCallback(
    async (kind: CatalogueKind, cursor?: string): Promise<CataloguePage> =>
      fetchCataloguePage(api, kind, cursor),
    [api],
  );

  const runInitial = useCallback(async () => {
    setPhase("loading");
    setError(null);
    setFailedAction("initial");
    try {
      const pages = await Promise.all(CATALOGUE_KINDS.map((kind) => fetchPage(kind)));
      setPagesByKind({
        product: [{ items: pages[0]!.items, nextCursor: pages[0]!.next_cursor }],
        service: [{ items: pages[1]!.items, nextCursor: pages[1]!.next_cursor }],
        module: [{ items: pages[2]!.items, nextCursor: pages[2]!.next_cursor }],
        campaign: [{ items: pages[3]!.items, nextCursor: pages[3]!.next_cursor }],
      });
      setPage(0);
      setPhase("ready");
    } catch (caught) {
      setError(caught);
      setPhase("error");
    }
  }, [fetchPage]);

  const lastCursorOf = (pages: KindPageState[]): string | null =>
    pages.length === 0 ? null : pages[pages.length - 1]!.nextCursor;

  const runNext = useCallback(async () => {
    setPhase("loading");
    setError(null);
    setFailedAction("next");
    const targets = CATALOGUE_KINDS.filter((kind) => lastCursorOf(pagesByKind[kind]) !== null);
    if (targets.length === 0) {
      setPhase("ready");
      return;
    }
    try {
      const fetched = await Promise.all(
        targets.map((kind) => fetchPage(kind, lastCursorOf(pagesByKind[kind])!)),
      );
      setPagesByKind((current) => {
        const copy: PagesByKind = { ...current };
        targets.forEach((kind, index) => {
          copy[kind] = [
            ...current[kind],
            { items: fetched[index]!.items, nextCursor: fetched[index]!.next_cursor },
          ];
        });
        return copy;
      });
      setPage((current) => current + 1);
      setPhase("ready");
    } catch (caught) {
      setError(caught);
      setPhase("error");
    }
  }, [fetchPage, pagesByKind]);

  useEffect(() => {
    void runInitial();
  }, [runInitial]);

  const runPrevious = useCallback(() => {
    setPage((current) => Math.max(0, current - 1));
  }, []);

  const hasData = CATALOGUE_KINDS.some((kind) => pagesByKind[kind].length > 0);
  const hasNext = CATALOGUE_KINDS.some((kind) => lastCursorOf(pagesByKind[kind]) !== null);
  const complete = useMemo(() => {
    const result = { ...COMPLETE_ALL };
    for (const kind of CATALOGUE_KINDS) {
      if (pagesByKind[kind].length === 0) {
        result[kind] = false;
        continue;
      }
      result[kind] = lastCursorOf(pagesByKind[kind]) === null;
    }
    return result;
  }, [pagesByKind]);

  return {
    page,
    hasNext,
    hasPrevious: page > 0,
    loading: phase === "loading",
    phase,
    error,
    retry: failedAction === "initial" ? runInitial : runNext,
    reload: runInitial,
    runInitial,
    runNext,
    runPrevious,
    hasData,
    complete,
    shown: (kind: CatalogueKind): CatalogueItem[] =>
      pagesByKind[kind].slice(0, page + 1).flatMap((entry) => entry.items),
  };
}

type DialogState = CatalogueDialog;

function hierarchyRows(
  data: AdminCatalogueResponse,
  complete: Record<CatalogueKind, boolean>,
): CatalogueDisplayRow[] {
  const rows: CatalogueDisplayRow[] = [];
  const seenServices = new Set<string>();
  const seenModules = new Set<string>();
  const seenCampaigns = new Set<string>();

  const addCampaigns = (moduleId: string, parentName: string | undefined) => {
    for (const campaign of data.campaigns.filter((item) => item.module_id === moduleId)) {
      seenCampaigns.add(campaign.id);
      rows.push({ kind: "campaign", item: campaign, parentMissing: false, parentName });
    }
  };
  const addModules = (serviceId: string, parentName: string | undefined) => {
    for (const module of data.modules.filter((item) => item.service_id === serviceId)) {
      seenModules.add(module.id);
      rows.push({ kind: "module", item: module, parentMissing: false, parentName });
      addCampaigns(module.id, module.name);
    }
  };
  const addServices = (productId: string, parentName: string | undefined) => {
    for (const service of data.services.filter((item) => item.product_id === productId)) {
      seenServices.add(service.id);
      rows.push({ kind: "service", item: service, parentMissing: false, parentName });
      addModules(service.id, service.name);
    }
  };

  for (const product of data.products) {
    rows.push({ kind: "product", item: product, parentMissing: false, parentName: undefined });
    addServices(product.id, product.name);
  }
  // A row whose parent is absent is "unavailable" only once the parent kind is exhausted;
  // while pages remain, the parent may still be on a later page.
  for (const service of data.services.filter((item) => !seenServices.has(item.id))) {
    seenServices.add(service.id);
    rows.push({ kind: "service", item: service, parentMissing: complete.product, parentName: undefined });
    addModules(service.id, undefined);
  }
  for (const module of data.modules.filter((item) => !seenModules.has(item.id))) {
    seenModules.add(module.id);
    rows.push({ kind: "module", item: module, parentMissing: complete.service, parentName: undefined });
    addCampaigns(module.id, undefined);
  }
  for (const campaign of data.campaigns.filter((item) => !seenCampaigns.has(item.id))) {
    rows.push({ kind: "campaign", item: campaign, parentMissing: complete.module, parentName: undefined });
  }
  return rows;
}

function statusSignal(status: CatalogueStatus): ChipSignal | undefined {
  if (status === "active") return "buy";
  if (status === "inactive") return "ctx";
  return undefined;
}

function mutationMessage(error: unknown): string {
  if (isApiError(error) && error.message === "The server response was invalid.") {
    return "The server response was invalid. No change was made.";
  }
  if (isApiError(error) && error.httpStatus === 403) {
    return "The server refused this catalogue change. No change was made.";
  }
  if (isApiError(error) && error.token === "catalogue_version_conflict") {
    return "This item changed after you opened it. Refresh the catalogue and try again. No change was made.";
  }
  if (isApiError(error) && error.token === "catalogue_retired") {
    return "This item is retired and cannot be changed. No change was made.";
  }
  if (isApiError(error) && error.token === "catalogue_parent_not_active") {
    return "This parent is not currently eligible. Refresh the catalogue and choose an eligible parent. No change was made.";
  }
  return "The catalogue change failed. No change was made. Try again.";
}

interface ParentChoice {
  item: CatalogueItem;
  eligible: boolean;
  reason?: string;
}

function isCurrentlyEffective(item: CatalogueItem, now: number): boolean {
  return item.status === "active" && Date.parse(item.effective_from) <= now &&
    (item.effective_to === null || Date.parse(item.effective_to) > now);
}

function parentChoices(data: AdminCatalogueResponse, kind: CatalogueKind): ParentChoice[] {
  const items = kind === "service"
    ? data.products
    : kind === "module"
      ? data.services
      : kind === "campaign"
        ? data.modules
        : [];
  const products = new Map(data.products.map((item) => [item.id, item]));
  const services = new Map(data.services.map((item) => [item.id, item]));
  const now = Date.now();

  return items.map((item) => {
    const chain: Array<CatalogueItem | undefined> = [item];
    if (kind === "module") {
      chain.push(products.get((item as ServiceCatalogueItem).product_id));
    }
    if (kind === "campaign") {
      const module = item as ModuleCatalogueItem;
      const service = services.get(module.service_id);
      chain.push(service, service ? products.get(service.product_id) : undefined);
    }
    const missingAt = chain.findIndex((entry) => entry === undefined);
    if (missingAt !== -1) return { item, eligible: false, reason: "parent hierarchy is incomplete" };
    const ineligibleAt = chain.findIndex((entry) => !isCurrentlyEffective(entry as CatalogueItem, now));
    if (ineligibleAt === -1) return { item, eligible: true };
    return {
      item,
      eligible: false,
      reason: ineligibleAt === 0 ? "parent is not currently effective" : "ancestor is not currently effective",
    };
  });
}

function CatalogueForm({
  dialog,
  catalogue,
  pending,
  error,
  notice,
  onClose,
  onSubmit,
}: {
  dialog: CatalogueDialog;
  catalogue: AdminCatalogueResponse;
  pending: boolean;
  error: unknown;
  notice?: string | undefined;
  onClose: () => void;
  onSubmit: (request: MutationRequest) => void;
}) {
  const [kind, setKind] = useState<CatalogueKind>(dialog.action === "create" ? "product" : dialog.kind);
  const [code, setCode] = useState("");
  const [name, setName] = useState(dialog.action === "create" ? "" : dialog.item.name);
  const [status, setStatus] = useState<"active" | "inactive">(
    dialog.action !== "create" && dialog.item.status === "inactive" ? "inactive" : "active",
  );
  const [parentId, setParentId] = useState("");
  const [reason, setReason] = useState("");
  const [validationError, setValidationError] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) {
      setValidationError("Reason must be at least 3 characters.");
      return;
    }
    if (dialog.action === "create") {
      const trimmedCode = code.trim();
      if (!/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/.test(trimmedCode)) {
        setValidationError("Code must use lower-case letters, digits and internal hyphens only.");
        return;
      }
      if (!trimmedName) {
        setValidationError("Name is required.");
        return;
      }
      if (kind !== "product" && !parentId) {
        setValidationError("Choose the exact parent for this item.");
        return;
      }
      onSubmit({
        action: "create",
        kind,
        body: {
          code: trimmedCode,
          name: trimmedName,
          ...(kind === "product" ? {} : { parent_id: parentId }),
          reason: trimmedReason,
        },
      });
      return;
    }
    if (dialog.action === "edit") {
      if (!trimmedName) {
        setValidationError("Name is required.");
        return;
      }
      onSubmit({
        action: "edit",
        kind: dialog.kind,
        item: dialog.item,
        body: {
          name: trimmedName,
          status,
          expected_version: dialog.item.version,
          reason: trimmedReason,
        },
      });
      return;
    }
    onSubmit({
      action: "retire",
      kind: dialog.kind,
      item: dialog.item,
      body: { expected_version: dialog.item.version, reason: trimmedReason },
    });
  };

  const title = dialog.action === "create"
    ? "Create catalogue item"
    : dialog.action === "edit"
      ? `Edit ${dialog.kind} ${dialog.item.name}`
      : `Retire ${dialog.kind} ${dialog.item.name}`;
  const actionLabel = dialog.action === "create"
    ? "Create item"
    : dialog.action === "edit"
      ? "Save changes"
      : "Confirm retirement";
  const options = parentChoices(catalogue, kind);

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      {...(dialog.action === "retire"
        ? { description: "This preserves the record as immutable history and cannot be undone here." }
        : {})}
      footer={(
        <>
          <Button type="button" variant="secondary" className="min-h-11" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button type="submit" form="catalogue-item-form" variant={dialog.action === "retire" ? "danger" : "primary"} className="min-h-11" loading={pending}>{actionLabel}</Button>
        </>
      )}
    >
      <form id="catalogue-item-form" className="flex flex-col gap-4" onSubmit={submit}>
        {dialog.action === "create" ? (
          <>
            <Select label="Kind" value={kind} onChange={(event) => { setKind(event.target.value as CatalogueKind); setParentId(""); }} disabled={pending}>
              <option value="product">Product</option>
              <option value="service">Service</option>
              <option value="module">Module</option>
              <option value="campaign">Campaign</option>
            </Select>
            {kind !== "product" ? (
              <Select label="Parent" value={parentId} onChange={(event) => setParentId(event.target.value)} disabled={pending}>
                <option value="">Choose a parent</option>
                {options.map(({ item, eligible, reason }) => <option key={item.id} value={item.id} disabled={!eligible}>{item.name} ({item.code}){reason ? `: ${reason}` : ""}</option>)}
              </Select>
            ) : null}
            <Input label="Code" value={code} onChange={(event) => setCode(event.target.value)} autoComplete="off" disabled={pending} hint="Codes are immutable after creation." />
            <Input label="Name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" disabled={pending} />
          </>
        ) : dialog.action === "edit" ? (
          <>
            <p className="rounded-lg bg-surface-muted px-3 py-2 text-sm text-ink">Code: {dialog.item.code}. Codes cannot be changed.</p>
            <Input label="Name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" disabled={pending} />
            <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value as "active" | "inactive")} disabled={pending}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </>
        ) : (
          <p className="text-sm text-ink">Confirm that {dialog.item.name} ({dialog.item.code}) should be retired.</p>
        )}
        {notice ? <p role="status" className="text-xs text-ink-muted">{notice}</p> : null}
        <Textarea label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} maxLength={500} disabled={pending} hint="Required for the server audit record." />
        {validationError || error ? (
          <p role="alert" aria-live="assertive" className="text-sm text-danger">
            {validationError || mutationMessage(error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function assembleCatalogue(products: CatalogueItem[], services: CatalogueItem[], modules: CatalogueItem[], campaigns: CatalogueItem[]): AdminCatalogueResponse {
  return {
    products: products as ProductCatalogueItem[],
    services: services as ServiceCatalogueItem[],
    modules: modules as ModuleCatalogueItem[],
    campaigns: campaigns as CampaignCatalogueItem[],
  };
}

export function CatalogueSection({ filter }: { filter: string }) {
  const pager = useCataloguePager();
  const api = useApiClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<CatalogueDialog | null>(null);
  // Complete option sets for the create dialog's parent picker, walked on demand.
  const [pickerData, setPickerData] = useState<AdminCatalogueResponse | null>(null);
  const [pendingPicker, setPendingPicker] = useState(false);

  const data = useMemo(
    () =>
      assembleCatalogue(
        pager.shown("product"),
        pager.shown("service"),
        pager.shown("module"),
        pager.shown("campaign"),
      ),
    // pager.shown is a plain read of the pager's state; the pager object identity changes
    // whenever that state changes, so depending on it alone is correct.
    [pager],
  );

  const ensurePickerComplete = useCallback(async () => {
    setPickerData(null);
    const missing = CATALOGUE_KINDS.filter((kind) => kind !== "campaign" && !pager.complete[kind]);
    if (missing.length === 0) return;
    setPendingPicker(true);
    try {
      for (const kind of missing) {
        let cursor: string | null = null;
        // Walk this kind's real cursors to exhaustion, appending as we go.
        for (;;) {
          const parsed = await fetchCataloguePage(api, kind, cursor ?? undefined);
          setPickerData((current) => {
            const base = current ?? data;
            return assembleCatalogue(
              kind === "product" ? [...base.products, ...parsed.items] : base.products,
              kind === "service" ? [...base.services, ...parsed.items] : base.services,
              kind === "module" ? [...base.modules, ...parsed.items] : base.modules,
              base.campaigns,
            );
          });
          if (parsed.next_cursor === null) break;
          cursor = parsed.next_cursor;
        }
      }
    } finally {
      setPendingPicker(false);
    }
  }, [api, pager.complete, data]);

  useEffect(() => {
    if (dialog?.action === "create") void ensurePickerComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog?.action]);

  const mutation = useMutation({
    mutationFn: async (request: MutationRequest) => {
      const path = request.action === "create"
        ? `/catalogue/${request.kind}`
        : request.action === "edit"
          ? `/catalogue/${request.kind}/${request.item?.id}`
          : `/catalogue/${request.kind}/${request.item?.id}/retire`;
      const response = request.action === "edit"
        ? await api.patch<unknown>(path, request.body, { requiresOrg: false, includeOrg: false })
        : await api.post<unknown>(path, request.body, { requiresOrg: false, includeOrg: false });
      const parsedResponse = parseMutationResponse(response, request.kind);
      if (parsedResponse === null) {
        throw invalidContract("The server response was invalid.");
      }
      return { action: request.action };
    },
    onSuccess: async ({ action }) => {
      await pager.reload();
      toast.push({
        tone: "success",
        title: action === "create"
          ? "Catalogue item created"
          : action === "edit"
            ? "Catalogue item updated"
            : "Catalogue item retired",
      });
      setDialog(null);
    },
  });

  return (
    /* The screen's one "Catalogue" heading is the ScreenFrame h1 (story P2-UI); the
       section keeps the region name for heading/landmark navigation without repeating
       the title (the U-2 walk pinned the single-header law). */
    <section aria-label="Catalogue" className="flex flex-col gap-4">
      {pager.phase === "ready" ? (
        <div className="flex flex-wrap items-end justify-end gap-3">
          <Button type="button" size="lg" onClick={() => { mutation.reset(); setDialog({ action: "create" }); }}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Create catalogue item
          </Button>
        </div>
      ) : null}

      {dialog ? (
        <CatalogueForm
          key={`${dialog.action}-${dialog.action === "create" ? "new" : dialog.item.id}`}
          dialog={dialog}
          catalogue={pickerData ?? data}
          pending={mutation.isPending}
          notice={pendingPicker ? "Loading all parent options…" : undefined}
          error={mutation.error}
          onClose={() => { if (!mutation.isPending) setDialog(null); }}
          onSubmit={(request) => { mutation.reset(); mutation.mutate(request); }}
        />
      ) : null}
      <CatalogueBody pager={pager} data={data} filter={filter} onOpenDialog={setDialog} />
    </section>
  );
}

function CatalogueBody({
  pager,
  data,
  filter,
  onOpenDialog,
}: {
  pager: ReturnType<typeof useCataloguePager>;
  data: AdminCatalogueResponse;
  filter: string;
  onOpenDialog: (dialog: DialogState) => void;
}) {
  if (pager.phase === "loading" && !pager.hasData) return <LoadingState label="Loading catalogue…" />;
  if (pager.phase === "error") {
    if (isApiError(pager.error) && pager.error.httpStatus === 403) {
      return <EmptyState title="You do not have permission to view the catalogue." body="The server refused this operator request." />;
    }
    return <ErrorState error={pager.error} onRetry={() => void pager.retry()} />;
  }

  const allRows = hierarchyRows(data, pager.complete);
  const rows = allRows.filter((row) => `${row.kind} ${row.item.code} ${row.item.name} ${row.item.status}`.toLocaleLowerCase().includes(filter));

  const openEdit = (row: CatalogueDisplayRow) => onOpenDialog({ action: "edit", kind: row.kind, item: row.item });
  const openRetire = (row: CatalogueDisplayRow) => onOpenDialog({ action: "retire", kind: row.kind, item: row.item });

  const actionsCell = (row: CatalogueDisplayRow) =>
    row.item.status === "retired" ? (
      <span className="text-xs text-ink-muted">History only</span>
    ) : (
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" className="min-h-11" onClick={() => openEdit(row)} aria-label={`Edit ${row.kind} ${row.item.name}`}>
          <Pencil className="h-4 w-4" aria-hidden="true" /> Edit
        </Button>
        <Button type="button" variant="danger" className="min-h-11" onClick={() => openRetire(row)} aria-label={`Retire ${row.kind} ${row.item.name}`}>
          <Trash2 className="h-4 w-4" aria-hidden="true" /> Retire
        </Button>
      </div>
    );

  const desktopRows: DataTableRow[] = rows.map((row) => ({
    id: `${row.kind}-${row.item.id}`,
    level: KIND_LABELS[row.kind],
    name: <span className="font-medium">{row.item.name}</span>,
    code: <span className="font-mono text-xs">{row.item.code}</span>,
    status: (
      <Chip signal={statusSignal(row.item.status)} dot={row.item.status === "active"}>
        {row.item.status}
      </Chip>
    ),
    parent: row.parentName ? (
      row.parentName
    ) : row.parentMissing ? (
      <span className="text-xs font-medium text-danger">Parent unavailable</span>
    ) : (
      "-"
    ),
    version: row.item.version,
    actions: actionsCell(row),
  }));

  const mobileRows: MobileRow[] = rows.map((row) => ({
    id: `${row.kind}-${row.item.id}`,
    name: row.item.name,
    value: `v${row.item.version}`,
    meta: `${KIND_LABELS[row.kind]} · ${row.item.code} · ${row.item.status}${row.parentName ? ` · under ${row.parentName}` : ""}${row.parentMissing ? " · Parent unavailable" : ""}`,
    signal: statusSignal(row.item.status),
    signalText: row.item.status,
    actions: row.item.status === "retired" ? (
      <span className="text-xs text-ink-muted">History only</span>
    ) : (
      <>
        <Button type="button" size="sm" variant="secondary" className="min-h-11 min-w-11" onClick={() => openEdit(row)} aria-label={`Edit ${row.kind} ${row.item.name}`}>Edit</Button>
        <Button type="button" size="sm" variant="danger" className="min-h-11 min-w-11" onClick={() => openRetire(row)} aria-label={`Retire ${row.kind} ${row.item.name}`}>Retire</Button>
      </>
    ),
  }));

  const columns = [
    { key: "level", label: "Level" },
    { key: "name", label: "Name" },
    { key: "code", label: "Code" },
    { key: "status", label: "Status" },
    { key: "parent", label: "Parent" },
    { key: "version", label: "Version", numeric: true },
    { key: "actions", label: "Actions" },
  ];

  return (
    <div className="flex flex-col gap-4" aria-busy={pager.loading || undefined}>
      {allRows.length === 0 ? (
        <EmptyState
          tone="first-run"
          title="No catalogue items yet"
          body="Create the first product to start the hierarchy."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          tone="filtered"
          title="No matching catalogue records"
          body="Clear or change the filter to review other catalogue records."
        />
      ) : (
        <>
          <div className="hidden md:block">
            <DataTable
              columns={columns}
              rows={desktopRows}
              label="Catalogue desktop view"
              caption="Catalogue hierarchy"
              density="compact"
            />
          </div>
          <MobileRows rows={mobileRows} label="Catalogue mobile view" className="md:hidden" />
          <Pagination
            label="Catalogue pagination"
            page={pager.page}
            hasPrevious={pager.hasPrevious}
            hasNext={pager.hasNext}
            onPrevious={() => pager.runPrevious()}
            onNext={() => void pager.runNext()}
          />
          {pager.loading ? <LoadingState label="Loading the next catalogue page…" /> : null}
        </>
      )}
    </div>
  );
}
